import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faHeart,
  faFire,
  faDatabase,
  faProjectDiagram,
  faWandMagicSparkles,
  faComments,
  faUsers
} from "@fortawesome/free-solid-svg-icons";
import { useCategories } from "../services/api";
import { openConsentPreferences } from "../utils/consent";
import { EXTENSION_URL, EXTENSION_NAME, EXTENSION_PITCH } from "../config/extension";
import "./Footer.css";

// SEO internal links — renders crawlable <a> tags for all smart list pages.
// Shows the first few; the rest stay in the DOM (hidden) so crawlers still see them.
const VISIBLE_LINKS = 10;

const FooterListLinks = ({ onMcpClick }) => {
  const { data: categories } = useCategories();
  const [expanded, setExpanded] = useState(false);
  if (!categories) return null;

  // Top-level filters only: a nested list (a sector's industry lists, under its "All <Sector>"
  // list) is in its parent's `children`, so the footer links the 11 sector lists and not the
  // ~140 industry ones. Every list stays reachable from /lists.
  const seen = new Set();
  const lists = categories
    .filter(cat => cat.dimension !== 'personal')
    .flatMap(cat => (cat.filters || []).map(f => ({ id: f.id, name: f.name })))
    .filter(f => { if (seen.has(f.id)) return false; seen.add(f.id); return true; });

  if (!lists.length) return null;

  // The MCP server exposes the lists, the signed-in watchlist and the market
  // read — so the pitch is "talk to your agent about the market", not "these
  // lists" (issue #276).
  const mcpCtaText = 'Talk to your AI agent about any stock list, your watchlist or today’s market — ChatGPT, Claude or any MCP client — via the QuantApe MCP Server →';
  const extensionCtaText = `${EXTENSION_PITCH} Get the ${EXTENSION_NAME} Chrome extension →`;

  return (
    <nav className="footer-list-links" aria-label="Browse smart lists">
      <p className="footer-list-links-title">Browse Smart Lists</p>
      <div className="footer-list-links-grid">
        {lists.map((list, i) => {
          const hidden = !expanded && i >= VISIBLE_LINKS;
          return (
            <a
              key={list.id}
              href={`/list/${list.id}`}
              style={hidden ? { display: 'none' } : undefined}
              tabIndex={hidden ? -1 : undefined}
            >
              {list.name}
            </a>
          );
        })}
        {lists.length > VISIBLE_LINKS && (
          <button
            type="button"
            className="footer-list-links-more"
            onClick={() => setExpanded(prev => !prev)}
            aria-expanded={expanded}
          >
            {expanded ? '‹ show less' : `${lists.length - VISIBLE_LINKS} more ›`}
          </button>
        )}
      </div>
      <div className="footer-list-links-ctas">
        <a className="footer-list-links-cta" href="/lists">
          See all lists →
        </a>
        {onMcpClick ? (
          <button type="button" className="footer-list-links-cta" onClick={onMcpClick}>
            {mcpCtaText}
          </button>
        ) : (
          <a className="footer-list-links-cta" href="/mcp">
            {mcpCtaText}
          </a>
        )}
        <a
          className="footer-list-links-cta"
          href={EXTENSION_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          {extensionCtaText}
        </a>
      </div>
    </nav>
  );
};

const Footer = ({ onMcpClick }) => {
  const handleConsentPreferences = (e) => {
    e.preventDefault();
    openConsentPreferences();
  };

  return (
    <footer className="footer">
      <div className="footer-mobile">
        <p className="footer-mobile-brand">First Principles Analytics</p>
        <p className="footer-mobile-tagline">Switch to desktop for the complete dashboard, tools, and research.</p>
        <div className="footer-mobile-links">
          <a href="/terms">Terms</a>
          <span>•</span>
          <a href="/privacy">Privacy</a>
          <span>•</span>
          <a
            href="https://discord.gg/YyXZPZ5Qdy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Community
          </a>
          <span>•</span>
          <a
            href="https://substack.com/@quantape"
            target="_blank"
            rel="noopener noreferrer"
          >
            Substack
          </a>
          <span>•</span>
          <a
            href="https://x.com/QuantApe171666"
            target="_blank"
            rel="noopener noreferrer"
          >
            X
          </a>
        </div>
        <p className="footer-mobile-copy">© {new Date().getFullYear()} First Principles Analytics, LLC</p>
      </div>

      <div className="footer-desktop">
        <FooterListLinks onMcpClick={onMcpClick} />
        <div className="footer-content">
          <div className="footer-left">
            <div className="footer-logo-section">
              <FontAwesomeIcon icon={faBolt} className="footer-logo-icon" />
              <div className="footer-logo-text">
                <span className="footer-logo-name">First Principles Analytics</span>
                <p className="footer-logo-subtitle">Because insight isn't optional. It's everything.</p>
              </div>
            </div>
            <div className="footer-made-with">
              <p>
                Built with{" "}
                <span className="icon-container" title="Love">
                  <FontAwesomeIcon icon={faHeart} />
                  <span className="tooltip">love</span>
                </span>{" "}
                and{" "}
                <span className="icon-container" title="Passion">
                  <FontAwesomeIcon icon={faFire} />
                  <span className="tooltip">passion</span>
                </span>{" "}
                in PNW, driven by the need for real, helpful, and timely{" "}
                <span className="icon-container" title="Analytics">
                  <FontAwesomeIcon icon={faProjectDiagram} style={{ color: '#007bff' }} />
                  <span className="tooltip">analytics</span>
                </span>
                , not a mountain of disparate{" "}
                <span className="icon-container" title="Data">
                  <FontAwesomeIcon icon={faDatabase} style={{ color: '#28a745' }} />
                  <span className="tooltip">data</span>
                </span>{" "}
                or after-the-fact{" "}
                <span className="icon-container" title="Opinions">
                  <FontAwesomeIcon icon={faComments} style={{ color: '#ffc107' }} />
                  <span className="tooltip">opinions</span>
                </span>
                . Assisted by{" "}
                <span className="icon-container" title="Generative AI">
                  <FontAwesomeIcon icon={faWandMagicSparkles} style={{ color: '#ff69b4' }} />
                  <span className="tooltip">GenAI</span>
                </span>
                .
              </p>
            </div>
            
            <p className="footer-copyright">
              &copy; {new Date().getFullYear()} DI SW VENTURES LLC. All rights reserved.
            </p>

            <div className="footer-links">
              <a href="/terms">Terms of Service</a>
              {" | "}
              <a href="/privacy">Privacy Policy</a>
              {" | "}
              <a href="/cookies">Cookie Policy</a>
              {" | "}
              <a href="#" onClick={handleConsentPreferences}>
                Consent Preferences
              </a>
              {" | "}
              <a href="mailto:admin@quantape.com?subject=Privacy%20Request">
                Privacy Request
              </a>
            </div>

            <div className="footer-social-links">
              <a
                href="https://discord.gg/YyXZPZ5Qdy"
                target="_blank"
                rel="noopener noreferrer"
                title="Join our Discord community"
              >
                <FontAwesomeIcon icon={faUsers} style={{ color: '#7289da' }} />
                Discord
              </a>
              <a
                href="https://substack.com/@quantape"
                target="_blank"
                rel="noopener noreferrer"
                title="Read our Substack"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"/></svg>
                Substack
              </a>
              <a
                href="https://x.com/QuantApe171666"
                target="_blank"
                rel="noopener noreferrer"
                title="Follow us on X"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                X
              </a>
            </div>
          </div>
          <div className="footer-right">
            <div className="footer-disclaimer">
              <p>
                The information provided on this website is for educational
                purposes only and does not constitute investment advice, financial
                advice, trading advice, or any other sort of advice. The content
                on this website is not intended to be and should not be
                interpreted as a recommendation to buy, sell, or hold any security
                or investment, nor does it represent an offer to buy or sell any
                security.
              </p>
              <p>
                Stock prices, financial data, and other market information displayed on this
                website may be delayed and are updated on a best-effort basis.
                This stock related data is provided by third-party providers and its accuracy, completeness,
                and timeliness cannot be guaranteed. Users should verify any critical information
                from authoritative sources before making investment decisions.
              </p>
              <p>
                The information on this website is general in nature and does not
                take into account your personal financial situation, goals, or
                risk tolerance. Before making any investment decisions, you should
                seek advice from a qualified and registered securities
                professional and conduct your own research and due diligence.
              </p>
              <p>
                This website and its content are provided "as is" and without any
                warranty, express or implied. The site owner/operator does not
                guarantee the accuracy, completeness, or timeliness of the
                information, and will not be liable for any errors or omissions,
                or for any actions taken based on the information provided.
              </p>
              
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
